import axios from 'axios'
import moment from 'moment-timezone'
import store from '../store/index'
import i18n from '../i18n/index'
import backendErrorMessage from './backendErrors'
import parseJwtPayload from './jwt'

const $http = axios.create()

$http.interceptors.request.use(async (cfg) => {
  if (!navigator.onLine) {
    store.dispatch('tools/setConnectionWarning', true)
    throw i18n.t('сервер_недоступен')
  }

  if (import.meta.env.VITE_API_ROOT_URL) {
    cfg.url = import.meta.env.VITE_API_ROOT_URL + cfg.url
  }

  const { bearerToken, userUid, impersonateSlackId } = await store.dispatch('auth/getBearerToken')
  cfg.headers.common.Authorization = `Bearer ${bearerToken}`
  cfg.headers['x-user-uid'] = userUid

  // Impersonate-сессия (вход по кастом-токену из бота): помечаем каждый запрос,
  // чтобы nginx мог логировать действия «от имени» (заголовок должен быть
  // в Access-Control-Allow-Headers на API-сервере)
  if (impersonateSlackId) {
    cfg.headers['x-impersonate-slack-id'] = impersonateSlackId
  }

  const timezone = moment.tz(moment.tz.guess()).format('Z')
  cfg.headers.common.timezone = timezone

  return cfg
}, (error) => {
  Promise.reject(error)
})

// Телеметрия расхода СМС: каждый форс-логаут = новая СМС, логируем причину.
// hours_since_auth считается от auth_time токена - кластер на ~24ч укажет
// на суточный TTL на бэкенде, NULL_TOKEN - на потерю currentUser клиентом
const signOutOnExpiredSession = (reason, requestConfig) => {
  const authHeader = (requestConfig && requestConfig.headers && (requestConfig.headers.Authorization || (requestConfig.headers.common && requestConfig.headers.common.Authorization))) || ''
  const authTime = parseJwtPayload(String(authHeader).replace('Bearer ', ''))?.auth_time
  store.dispatch('analytics/logEvent', {
    eventName: 're_auth_forced_signout',
    eventProperties: {
      reason,
      request_path: ((requestConfig && requestConfig.url) || '').replace(import.meta.env.VITE_API_ROOT_URL, ''),
      hours_since_auth: authTime ? Math.round(((Date.now() / 1000 - authTime) / 3600) * 10) / 10 : undefined,
    },
  })
  store.dispatch('tools/showSnackbar', { message: i18n.t('к_сожалению_ваш_сеанс_истек') })
  store.dispatch('auth/signOut')
}

$http.interceptors.response.use((cfg) => cfg, (error) => {
  if (error.response && error.response.status && String(error.response.status).startsWith('5')) {
    store.dispatch('tools/setConnectionWarning', true)
  } else if (error.response && error.response.status === 401) {
    // Токен мог протухнуть из-за сбоя нативного refresh — пробуем один раз обновить
    // и повторить запрос, разлогиниваем только если 401 повторился
    const cfg = error.config
    if (cfg && !cfg.retriedAfter401) {
      cfg.retriedAfter401 = true
      return store.dispatch('auth/getBearerToken', { forceRefresh: true }).then(({ bearerToken, userUid }) => {
        if (!bearerToken) {
          signOutOnExpiredSession('NULL_TOKEN', cfg)
          return Promise.reject(error)
        }
        cfg.headers.Authorization = `Bearer ${bearerToken}`
        cfg.headers['x-user-uid'] = userUid
        // cfg.url уже абсолютный — повторяем через чистый axios, минуя request-интерцептор
        return axios.request(cfg).catch((retryError) => {
          if (retryError.response && retryError.response.status === 401) {
            signOutOnExpiredSession('BACKEND_401', cfg)
          }
          return Promise.reject(retryError)
        })
      })
    }
    signOutOnExpiredSession('BACKEND_401', error.config)
  } else if (error.response) {
    const message = (error.response.data && backendErrorMessage(error.response.data.code, error.response.data.meta)) || (error.response.data && error.response.data.message)
    if (message) {
      store.dispatch('tools/showSnackbar', { message: `${message}`, url: error.response.data.url })
    }
  } else if (error.request) {
    if (error && error.message === 'Network Error') {
      store.dispatch('tools/setConnectionWarning', true)
    } else {
      store.dispatch('tools/showSnackbar', { message: `${i18n.t('произошла_ошибка')}:  ${error.message}` })
    }
  } else {
    store.dispatch('tools/showSnackbar', { message: `${i18n.t('произошла_ошибка')}:  ${error}` })
  }
  return Promise.reject(error)
})

export default $http
